/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: =, Brian Bal
 */
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { connect } from "react-redux"
import {
    Color,
    Modal,
    Row,
    ModalTitle,
    TitleSection,
    ContentSection,
    Button,
    Icon,
    useToken,
    FullGameDownloadNotAvailableContent,
    trackEvent
} from "trux"
import * as links from "../../util/links"
import { traceIdMomentUrl } from "../../util/links"
import DownloadMomentWrapper from "../DownloadMomentWrapper"
import { nativeShareMoment } from "../ShareDialog/ShareGameModal"
import { calculateNoOfDaysBeforeExpiration } from "../../util/helpers"
import { FullVideoWarning } from "../FullVideoWarning/FullVideoWarning"
import { useGetMomentCollectionInfoQuery } from "../../generated/graphql"
import "./MomentOptions.css"
import { AutoAddMomentToTraceiDDialog } from "../AutoAddMomentToTraceiDDialog"

const MomentOptionsDialogComp = ({
    isFullGame,
    fullGameEventNumber,
    fullGameTime,
    currentPlaylist,
    event,
    camera,
    user,
    game,
    team,
    teamName,
    gameId,
    extras,
    history,
    createEnabled,
    removeGoalEnabled,
    editEnabled,
    acl,
}) => {
    let open = !!(extras && extras.options)
    const currentSource = useSelector((state) => {
        return state.video.value.source
    })

    let [mode, setMode] = useState("options")
    let urlParts = history.location.pathname.split("/")
    let en = event?.event_number
    if (event) {
        if (camera && camera !== "all" && camera !== "tracecam") {
            en = en += `:${camera}`
        }
        urlParts[5] = en
    }
    let editedUrl = urlParts.join("/")
    let uploadTIme = game?.subtasks_statuses?.webapp_migration?.update_time
    let expirationDays =
        uploadTIme && calculateNoOfDaysBeforeExpiration(uploadTIme)
    let isFullVideoExpired =
        !expirationDays && currentPlaylist?.id === "full-game"
    let isFullVideoAvailable =
        expirationDays && currentPlaylist?.id === "full-game"
    let fullGameLink = `/${teamName}/games/${gameId}/full-game/${fullGameEventNumber}/time/${fullGameTime}`
    let createLink =
        editedUrl.replace("/options", "/create-moment") +
        `/start=${fullGameTime}`
    let removeLink = editedUrl.replace("/options", "/do/remove-goal")
    let editLink =
        editedUrl.replace("/options", "/edit-moment") + `/start=${fullGameTime}`
    let downloadLink =
        editedUrl.replace("/options", "/download-options") +
        `/start=${fullGameTime}`

    let saveUrl = links.saveMomentUrl(teamName, currentPlaylist?.id, gameId, en)

    const token = useToken("MomentOptionsDialogComp")
    const tokenReady = token !== null

    const {
        data: momentCollectionData,
        loading: momentCollectionLoading,
        error: momentCollectionError,
    } = useGetMomentCollectionInfoQuery({
        variables: {
            token: token,
            game_clip_id: currentSource?.game_clip_id,
            user_id: token?.user_id,
        },
        skip: !tokenReady || !event?.game_clip_id,
    })

    const isMomentAdded = momentCollectionData?.momentCollectionInfo?.length

    const onTraceIdClick = (e) => {
        // If this is a family moment, track it
        const isFamily = team?.division?.type === "family"
        if (isFamily && e?.target) {
            const eventAction = "Add to Trace iD Pressed (Family)"
            trackEvent({
                action: eventAction,
                label: "Add to Trace iD Pressed (Family)",
                element: e.target
            })
        }
        if (isMomentAdded) {
            let momentInfo = momentCollectionData.momentCollectionInfo[0]
            window.location.href = traceIdMomentUrl(
                momentInfo.playlist_hash_key,
                momentInfo.collection_hash_key,
                momentInfo.user_playlist_clip_id,
                acl
            )
        } else {
            if (saveUrl.startsWith("http")) {
                window.location.href = saveUrl
            } else {
                history.push(saveUrl)
            }
        }
    }

    useEffect(() => {
        if (open) {
            document.dispatchEvent(new CustomEvent("trc-pause"))
        }
    })

    const close = (e) => {
        history.push(editedUrl.replace("/options", ""))
        setTimeout(() => {
            setMode("options")
        }, 1000)
    }

    let btnWidth = "96px"
    let btnMarBtm = "4"

    const canNativeShare = () => {
        return navigator.share
    }

    const handleButtonOnClick = (e, type, link) => {
        switch (type) {
            case "download":
            case "edit-tags":
            case "full-game":
            case "create-moment":
            case "remove-goal": {
                history.push(link)
                break
            }
            default: {
                break
            }
        }
    }

    return (
        <>
            {mode === "auto-add" && (
                <AutoAddMomentToTraceiDDialog
                    open={true}
                    team={team}
                    token={token}
                    moment={event}
                    title={"Link copied"}
                    disableAutoAdd={event?.type === "FullGameVideo"}
                />
            )}
            {mode !== "auto-add" && (
                <Modal
                    trkContext="Moment Options"
                    className="MomentOptions"
                    open={open}
                    onShadeClick={close}
                >
                    <TitleSection onCloseClick={close}>
                        <ModalTitle content="Moment options" />
                    </TitleSection>
                    {mode === "download" &&
                        currentPlaylist?.id !== "full-game" && (
                            <DownloadMomentWrapper
                                onClose={() => setMode("options")}
                            />
                        )}
                    {mode === "download" && isFullVideoAvailable && (
                        <DownloadMomentWrapper
                            onClose={() => setMode("options")}
                            expirationDays={expirationDays}
                        />
                    )}
                    {mode === "download" && isFullVideoExpired && (
                        <FullGameDownloadNotAvailableContent
                            history={history}
                            pathToReplace={"options"}
                        />
                    )}
                    {mode === "options" && (
                        <ContentSection>
                            <Row
                                center
                                alignItems="flex-start"
                                justifyContent="space-evenly"
                            >
                                <Button
                                    width={btnWidth}
                                    marginBottom={btnMarBtm}
                                    trkAction="Download Pressed"
                                    onClick={(e) =>
                                        handleButtonOnClick(
                                            e,
                                            "download",
                                            downloadLink
                                        )
                                    }
                                    icon={Icon.Type.Download}
                                    content="Download"
                                />
                                {isFullGame !== true && (
                                    <Button
                                        width={btnWidth}
                                        marginBottom={btnMarBtm}
                                        trkAction="Edit Tags"
                                        onClick={(e) =>
                                            handleButtonOnClick(
                                                e,
                                                "edit-tags",
                                                editLink
                                            )
                                        }
                                        icon={Icon.Type.Tag}
                                        content="Edit tags"
                                    />
                                )}
                                {isFullGame !== true && (
                                    <Button
                                        width={btnWidth}
                                        marginBottom={btnMarBtm}
                                        trkAction="Full Game Pressed"
                                        onClick={(e) =>
                                            handleButtonOnClick(
                                                e,
                                                "full-game",
                                                fullGameLink
                                            )
                                        }
                                        icon={Icon.Type.Field}
                                        content={"Go to full game"}
                                    />
                                )}
                                {removeGoalEnabled && (
                                    <Button
                                        width={btnWidth}
                                        marginBottom={btnMarBtm}
                                        trkAction="Remove Goal Pressed"
                                        onClick={(e) =>
                                            handleButtonOnClick(
                                                e,
                                                "remove-goal",
                                                removeLink
                                            )
                                        }
                                        icon={Icon.Type.Close}
                                        color={Color.Warning}
                                        content="This is not a goal"
                                    />
                                )}
                                {canNativeShare() && (
                                    <Button
                                        trkAction="Share To"
                                        trkLabel="Share Button"
                                        width={btnWidth}
                                        onClick={() => {
                                            // console.log(
                                            //     `currentPl is ${JSON.stringify(
                                            //         currentPlaylist
                                            //     )}`
                                            // )
                                            nativeShareMoment(
                                                game,
                                                currentPlaylist,
                                                event,
                                                user
                                            ).then(() => {
                                                setMode("auto-add")
                                            })
                                        }}
                                        icon={Icon.Type.Action}
                                        content="Share to"
                                        marginBottom={btnMarBtm}
                                    />
                                )}
                                 {(isFullGame !== true && isMomentAdded === true) && (
                                    <Button
                                        width={btnWidth}
                                        marginBottom={btnMarBtm}
                                        trkAction="Add to Trace iD Pressed"
                                        onClick={onTraceIdClick}
                                        icon={
                                            isMomentAdded
                                                ? Icon.Type.TraceIDFilled
                                                : Icon.Type.TraceIDEmpty
                                        }
                                        content={
                                            (isMomentAdded ? "Go " : "Add ") +
                                            "to Trace iD"
                                        }
                                    />
                                 )}
                            </Row>
                            {isFullVideoAvailable && (
                                <FullVideoWarning
                                    marginTop="8"
                                    expirationDays={expirationDays}
                                />
                            )}
                        </ContentSection>
                    )}
                </Modal>
            )}
        </>
    )
}

const mapStateToProps = (state, props) => {
    let extras = links.getUrlExtras(window.location.hash)
    let teamName = props.match.params.team_name
    let gameId = props.match.params.game_id
    let fullGameEventNumber = 1
    let fullGameTime = 0
    let isFullGame = false
    let createEnabled = false
    let removeGoalEnabled = false
    let editEnabled = false
    let game = state.game.value
    let team = state.team.value
    let user = state.user.value
    let event = null
    let currentPlaylist = state.video.value.playlist
    let acl = state.acl.value
    let camera = state.video.value.camera

    if (state.video.value.event) {
        isFullGame = state.video.value.event.meta.type === "FullGameVideo"
    }

    if (state.acl.value && state.video.value.event) {
        createEnabled = state.acl.value.create_moment_tool
        editEnabled = state.video.value.event.meta.type !== "FullGameVideo"
        removeGoalEnabled = state.video.value.event.meta.type.includes("key")
    }

    if (state.game.value) {
        event = state.video.value.event
        let halfEvent = null
        let allEvents = state.game.value.events

        if (event) {
            for (let ev of allEvents) {
                if (ev.meta.type === "FullGameVideo") {
                    let hStart = ev.full_game_time
                    let hEnd = hStart + ev.dynamic.duration
                    if (
                        hStart <= event.full_game_time &&
                        event.full_game_time < hEnd
                    ) {
                        halfEvent = ev
                        fullGameEventNumber = ev.event_number
                        break
                    }
                }
            }
            if (event.meta.type === "FullGameVideo") {
                fullGameTime = event.full_game_time
            } else if (halfEvent) {
                fullGameTime = event.full_game_time
            }
        }
        let video = document.querySelector(
            `.VideoPlayer video.main-video[data-active=\"true\"]`
        )
        if (video) {
            // console.log("fullGameTime video time offset", video.currentTime)
            fullGameTime += video.currentTime
        }
        fullGameTime = Math.round(fullGameTime)

        if (currentPlaylist === null) {
            currentPlaylist = { title: "", event_count: 0 }
        }
    }

    return {
        extras,
        camera,
        team,
        teamName,
        gameId,
        fullGameEventNumber,
        fullGameTime,
        isFullGame,
        createEnabled,
        removeGoalEnabled,
        editEnabled,
        currentPlaylist,
        game,
        user,
        event,
        acl,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {}
}

const MomentOptionsDialog = connect(
    mapStateToProps,
    mapDispatchToProps
)(MomentOptionsDialogComp)
export default MomentOptionsDialog
